import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Banknote,
  Columns,
  Combine,
  Copy,
  Edit,
  FileDown,
  FileText,
  Loader2,
  Plus,
  Search,
  Trash2,
} from 'lucide-react'
import toast from 'react-hot-toast'

import { showDeleteConfirm, showSuccess } from '@/lib/swal'
import { goodsReturnService } from '@/services/goodsReturnService'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import type { GoodsReturnNoteRow } from '../types'

const PAGE_SIZE = 10

function goodsReturnErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) return error.message.trim()
  if (error && typeof error === 'object' && 'response' in error) {
    const data = (error as { response?: { data?: unknown } }).response?.data
    if (data && typeof data === 'object') {
      const root = data as Record<string, unknown>
      const result = root.result
      if (result && typeof result === 'object') {
        const msg = (result as Record<string, unknown>).msg
        if (typeof msg === 'string' && msg.trim()) return msg.trim()
      }
      if (typeof root.message === 'string' && root.message.trim()) return root.message.trim()
    }
  }
  return fallback
}

type DataColumnKey =
  | 'code'
  | 'reference'
  | 'warehouse'
  | 'vendor'
  | 'grnCode'
  | 'date'
  | 'total'

const DATA_COLUMNS: { key: DataColumnKey; label: string }[] = [
  { key: 'code', label: 'Code' },
  { key: 'reference', label: 'Reference' },
  { key: 'warehouse', label: 'Warehouse' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'grnCode', label: 'Grn Code' },
  { key: 'date', label: 'Date' },
  { key: 'total', label: 'Total' },
]

export const GoodsReturnNotesList: React.FC = () => {
  const navigate = useNavigate()
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)

  const [rows, setRows] = useState<GoodsReturnNoteRow[]>([])
  const [loading, setLoading] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [combinedRefundSubmitting, setCombinedRefundSubmitting] = useState(false)
  const [selectedReturnIds, setSelectedReturnIds] = useState<number[]>([])
  const [convertingRefundId, setConvertingRefundId] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [visibleColumns, setVisibleColumns] = useState<Record<DataColumnKey, boolean>>({
    code: true,
    reference: true,
    warehouse: true,
    vendor: true,
    grnCode: true,
    date: true,
    total: true,
  })

  useEffect(() => {
    setPageNumber(1)
  }, [branchId])

  useEffect(() => {
    if (!branchId) {
      setRows([])
      setTotalCount(0)
      return
    }
    const load = async () => {
      try {
        setLoading(true)
        const res = await goodsReturnService.getAllGoodsReturns(branchId, PAGE_SIZE, pageNumber)
        setRows(res.items)
        setTotalCount(res.totalCount)
      } catch (error: unknown) {
        setRows([])
        setTotalCount(0)
        const msg =
          error && typeof error === 'object' && 'response' in error
            ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined
        toast.error(msg || 'Failed to load goods return notes.')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [branchId, pageNumber])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      [r.code, r.reference, r.warehouse, r.vendor, r.grnCode, r.date, r.total].some((x) =>
        String(x).toLowerCase().includes(q),
      ),
    )
  }, [rows, searchQuery])

  const eligibleForCombined = useMemo(
    () => filtered.filter((r) => Number.isFinite(r.id) && r.id > 0 && !r.isRefundCreated),
    [filtered],
  )

  const eligibleIdSet = useMemo(() => new Set(eligibleForCombined.map((r) => r.id)), [eligibleForCombined])

  const allEligibleSelected =
    eligibleForCombined.length > 0 && eligibleForCombined.every((r) => selectedReturnIds.includes(r.id))

  useEffect(() => {
    setSelectedReturnIds((prev) => prev.filter((id) => eligibleIdSet.has(id)))
  }, [eligibleIdSet])

  const toggleSelectRow = (id: number) => {
    setSelectedReturnIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const toggleSelectAllEligible = () => {
    if (allEligibleSelected) {
      const drop = new Set(eligibleForCombined.map((r) => r.id))
      setSelectedReturnIds((prev) => prev.filter((id) => !drop.has(id)))
    } else {
      setSelectedReturnIds((prev) => {
        const next = new Set(prev)
        for (const r of eligibleForCombined) next.add(r.id)
        return [...next]
      })
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const startIdx = totalCount === 0 ? 0 : (pageNumber - 1) * PAGE_SIZE + 1
  const endIdx = Math.min(pageNumber * PAGE_SIZE, totalCount)

  const fmtDate = (value?: string) => {
    if (!value) return '-'
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleDateString('en-GB')
  }

  const fmtTotal = (n: number) => (Number.isFinite(n) ? n.toFixed(2) : String(n))

  const visibleDataCount = Object.values(visibleColumns).filter(Boolean).length
  const tableColSpan = visibleDataCount + 4

  const toggleColumn = (key: DataColumnKey) =>
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))

  const handleCopy = () => {
    if (filtered.length === 0) return toast.error('Nothing to copy.')
    const header = ['Code', 'Reference', 'Warehouse', 'Vendor', 'Grn Code', 'Date', 'Total']
    const lines = filtered.map((r) =>
      [r.code, r.reference, r.warehouse, r.vendor, r.grnCode, r.date, fmtTotal(r.total)].join('\t'),
    )
    void navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success('Copied.')
  }

  const handleDelete = async (r: GoodsReturnNoteRow) => {
    if (!branchId || !r.id || r.id <= 0) {
      toast.error('Cannot delete this record.')
      return
    }
    const label = r.code?.trim() || r.reference?.trim() || `Return #${r.id}`
    const ok = await showDeleteConfirm(label)
    if (!ok) return
    try {
      setDeletingId(r.id)
      await goodsReturnService.delete(r.id)
      await showSuccess('Deleted', 'Goods return note was deleted.')
      const res = await goodsReturnService.getAllGoodsReturns(branchId, PAGE_SIZE, pageNumber)
      setRows(res.items)
      setTotalCount(res.totalCount)
      if (res.items.length === 0 && pageNumber > 1) {
        setPageNumber((p) => Math.max(1, p - 1))
      }
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined
      toast.error(msg || 'Failed to delete goods return note.')
    } finally {
      setDeletingId(null)
    }
  }

  const handleConvertToRefund = async (r: GoodsReturnNoteRow) => {
    if (r.isRefundCreated) {
      toast.error('This goods return is already converted to a vendor credit note.')
      return
    }
    if (!branchId || !r.id || r.id <= 0) {
      toast.error('Cannot convert this record.')
      return
    }
    try {
      setConvertingRefundId(r.id)
      const msg = await goodsReturnService.convertToRefund(r.id)
      await showSuccess(
        'Vendor credit posted',
        msg || 'The goods return was converted to a posted vendor credit note.',
      )
      const res = await goodsReturnService.getAllGoodsReturns(branchId, PAGE_SIZE, pageNumber)
      setRows(res.items)
      setTotalCount(res.totalCount)
    } catch (error: unknown) {
      toast.error(goodsReturnErrorMessage(error, 'Failed to convert to refund.'))
    } finally {
      setConvertingRefundId(null)
    }
  }

  const handleCombinedRefund = async () => {
    if (!branchId) {
      toast.error('Select a branch first.')
      return
    }
    const ids = selectedReturnIds.filter((id) => eligibleIdSet.has(id))
    if (ids.length === 0) {
      toast.error('Select at least one goods return note.')
      return
    }
    try {
      setCombinedRefundSubmitting(true)
      const msg = await goodsReturnService.combinedRefund(ids)
      setSelectedReturnIds([])
      await showSuccess(
        'Vendor credits posted',
        msg || 'Selected goods returns were converted to posted vendor credit notes.',
      )
      const res = await goodsReturnService.getAllGoodsReturns(branchId, PAGE_SIZE, pageNumber)
      setRows(res.items)
      setTotalCount(res.totalCount)
    } catch (error: unknown) {
      toast.error(goodsReturnErrorMessage(error, 'Failed to process combined refund.'))
    } finally {
      setCombinedRefundSubmitting(false)
    }
  }

  const handleExportCSV = () => {
    if (filtered.length === 0) return toast.error('Nothing to export.')
    const header = 'Code,Reference,Warehouse,Vendor,GrnCode,Date,Total'
    const body = filtered
      .map((r) =>
        [r.code, r.reference, r.warehouse, r.vendor, r.grnCode, r.date, fmtTotal(r.total)]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(','),
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `goods_return_notes_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success('CSV downloaded.')
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Goods Return Notes</h1>
            <p className="text-muted-foreground text-sm">Goods Return Note Details</p>
          </div>
        </div>

        <Button onClick={() => navigate('/inventory/goods-return-notes/new')} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          Add Goods Return Note
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search (current page)"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                  }}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => void handleCombinedRefund()}
                disabled={
                  combinedRefundSubmitting ||
                  deletingId !== null ||
                  convertingRefundId !== null ||
                  loading ||
                  !branchId ||
                  selectedReturnIds.length === 0
                }
              >
                {combinedRefundSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Combine className="mr-2 h-4 w-4" />
                )}
                Combined refund{selectedReturnIds.length > 0 ? ` (${selectedReturnIds.length})` : ''}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" type="button">
                    <Columns className="mr-2 h-4 w-4" />
                    Column Visibility
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Show columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {DATA_COLUMNS.map((col) => (
                    <DropdownMenuCheckboxItem
                      key={col.key}
                      checked={visibleColumns[col.key]}
                      onCheckedChange={() => toggleColumn(col.key)}
                    >
                      {col.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  <TableHead className="h-12 w-[52px] bg-primary/5 px-2 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    <div className="flex justify-center">
                      <Checkbox
                        checked={allEligibleSelected}
                        disabled={
                          eligibleForCombined.length === 0 ||
                          combinedRefundSubmitting ||
                          loading ||
                          convertingRefundId !== null ||
                          deletingId !== null
                        }
                        onCheckedChange={() => toggleSelectAllEligible()}
                        aria-label="Select all goods return notes on this page"
                      />
                    </div>
                  </TableHead>
                  {visibleColumns.code && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Code
                    </TableHead>
                  )}
                  {visibleColumns.reference && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Reference
                    </TableHead>
                  )}
                  {visibleColumns.warehouse && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Warehouse
                    </TableHead>
                  )}
                  {visibleColumns.vendor && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Vendor
                    </TableHead>
                  )}
                  {visibleColumns.grnCode && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Grn Code
                    </TableHead>
                  )}
                  {visibleColumns.date && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Date
                    </TableHead>
                  )}
                  {visibleColumns.total && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Total
                    </TableHead>
                  )}
                  <TableHead className="h-12 min-w-[7rem] bg-primary/5 px-2 text-center text-xs font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    Refund
                  </TableHead>
                  <TableHead className="h-12 w-[88px] bg-primary/5 px-2 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    Edit
                  </TableHead>
                  <TableHead className="h-12 w-[88px] bg-primary/5 px-2 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    Delete
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!branchId ? (
                  <TableRow>
                    <TableCell colSpan={tableColSpan} className="py-12 text-center text-muted-foreground">
                      Select a branch to load goods return notes.
                    </TableCell>
                  </TableRow>
                ) : loading ? (
                  <TableRow>
                    <TableCell colSpan={tableColSpan} className="py-12 text-center text-muted-foreground">
                      <div className="p-12 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        <p className="mt-4 text-muted-foreground">Loading…</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tableColSpan} className="py-12 text-center text-muted-foreground">
                      No data available in table
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((r, i) => {
                    const converted = Boolean(r.isRefundCreated)
                    const canSelect = Number.isFinite(r.id) && r.id > 0 && !converted
                    const convertDisabled =
                      converted ||
                      loading ||
                      deletingId !== null ||
                      convertingRefundId !== null ||
                      combinedRefundSubmitting ||
                      r.id <= 0
                    return (
                    <TableRow
                      key={r.id > 0 ? r.id : `row-${pageNumber}-${i}`}
                      className="border-b transition-colors hover:bg-primary/5 dark:hover:bg-primary/10"
                    >
                      <TableCell className="w-[52px] px-2 py-2 text-center">
                        {canSelect ? (
                          <div className="flex justify-center">
                            <Checkbox
                              checked={selectedReturnIds.includes(r.id)}
                              disabled={
                                combinedRefundSubmitting ||
                                loading ||
                                convertingRefundId !== null ||
                                deletingId !== null
                              }
                              onCheckedChange={() => toggleSelectRow(r.id)}
                              aria-label={`Select goods return note ${r.code || r.id}`}
                            />
                          </div>
                        ) : null}
                      </TableCell>
                      {visibleColumns.code && (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.code || '-'}</TableCell>
                      )}
                      {visibleColumns.reference && (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.reference || '-'}</TableCell>
                      )}
                      {visibleColumns.warehouse && (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.warehouse || '-'}</TableCell>
                      )}
                      {visibleColumns.vendor && (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.vendor || '-'}</TableCell>
                      )}
                      {visibleColumns.grnCode && (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.grnCode || '-'}</TableCell>
                      )}
                      {visibleColumns.date && (
                        <TableCell className="px-4 py-2 text-center text-sm">{fmtDate(r.date)}</TableCell>
                      )}
                      {visibleColumns.total && (
                        <TableCell className="px-4 py-2 text-center text-sm">{fmtTotal(r.total)}</TableCell>
                      )}
                      <TableCell className="px-2 py-2 text-center">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className={
                            converted
                              ? 'h-8 gap-1 px-2 text-xs opacity-40'
                              : 'h-8 gap-1 px-2 text-xs'
                          }
                          title={converted ? 'Already converted to refund' : 'Convert to refund'}
                          aria-label={converted ? 'Already converted to refund' : 'Convert to refund'}
                          disabled={convertDisabled}
                          onClick={() => void handleConvertToRefund(r)}
                        >
                          {convertingRefundId === r.id ? (
                            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                          ) : (
                            <Banknote className="h-3.5 w-3.5 shrink-0" />
                          )}
                          <span className="hidden sm:inline">Convert</span>
                        </Button>
                      </TableCell>
                      <TableCell className="px-2 py-2 text-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                          title="Edit"
                          aria-label="Edit"
                          disabled={
                            loading ||
                            deletingId !== null ||
                            convertingRefundId !== null ||
                            combinedRefundSubmitting ||
                            r.id <= 0
                          }
                          onClick={() => navigate(`/inventory/goods-return-notes/${r.id}/edit`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                      <TableCell className="px-2 py-2 text-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          title="Delete"
                          aria-label="Delete"
                          disabled={
                            loading ||
                            deletingId !== null ||
                            convertingRefundId !== null ||
                            combinedRefundSubmitting
                          }
                          onClick={() => void handleDelete(r)}
                        >
                          {deletingId === r.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col items-center justify-between gap-4 border-t px-4 py-4 sm:flex-row sm:px-6">
            <p className="text-center text-sm text-muted-foreground sm:text-left">
              Showing {startIdx} to {endIdx} of {totalCount} entries
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={loading || pageNumber <= 1 || !branchId}
              >
                Prev
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-medium">
                {pageNumber} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                disabled={loading || pageNumber >= totalPages || !branchId}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
